import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { HexColorPicker } from "react-colorful";

interface ColorStop {
  color: string;
  opacity: number;
}

interface GradientValue {
  from: ColorStop;
  to: ColorStop;
}

interface ColorGradientPickerProps {
  defaultFrom?: ColorStop;
  defaultTo?: ColorStop;
  onChange?: (value: GradientValue) => void;
}

function hexToRgba(hex: string, opacity: number): string {
  const safeHex = hex.length === 7 ? hex : "#000000";
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
}

interface SingleRowProps {
  value: ColorStop;
  onChange: (value: ColorStop) => void;
  showTransparency?: boolean;
}

function SingleColorRow({
  value,
  onChange,
  showTransparency = true,
}: SingleRowProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleHex = (raw: string) => {
    const cleaned = raw.startsWith("#") ? raw : "#" + raw;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(cleaned)) {
      onChange({ ...value, color: cleaned.toUpperCase() });
    }
  };

  const handleOpacity = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      opacity: Math.min(100, Math.max(0, Number(e.target.value))),
    });
  };

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "flex", gap: 6, width: "100%" }}
    >
      {/* Color swatch + hex input */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "transparent",
          border: open
            ? "1px solid rgba(198,217,44,0.5)"
            : "1px solid rgba(255,255,255,0.12)",
          borderRadius: 6,
          padding: "5px 8px",
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            background: hexToRgba(value.color, value.opacity),
            border: "1px solid rgba(255,255,255,0.15)",
            flexShrink: 0,
          }}
        />
        <input
          value={value.color}
          onChange={(e) => handleHex(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: "rgba(255,255,255,0.85)",
            fontFamily: "inherit",
            fontSize: 12,
            width: "100%",
            minWidth: 0,
          }}
        />
      </div>

      {/* Opacity input */}
      {showTransparency && (
        <div
          style={{
            width: 72,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 6,
            padding: "5px 8px",
          }}
        >
          <input
            type="number"
            min={0}
            max={100}
            value={value.opacity}
            onChange={handleOpacity}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "rgba(255,255,255,0.85)",
              fontFamily: "inherit",
              fontSize: 12,
              width: "100%",
              textAlign: "right",
            }}
          />
          <span
            style={{
              color: "rgba(255,255,255,0.35)",
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            %
          </span>
        </div>
      )}

      {/* Color picker popup */}
      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 1100,
            top: "calc(100% + 6px)",
            left: 0,
            background: "#1A1A1A",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            padding: 12,
            boxShadow: "0 12px 40px rgba(0,0,0,0.7)",
          }}
        >
          <HexColorPicker
            color={value.color}
            onChange={(c: string) =>
              onChange({ ...value, color: c.toUpperCase() })
            }
            style={{ width: 180 }}
          />
          <div
            style={{
              marginTop: 6,
              textAlign: "center",
              fontSize: 11,
              color: "rgba(255,255,255,0.4)",
            }}
          >
            {value.color.toUpperCase()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ColorGradientPicker({
  defaultFrom = { color: "#C6D92C", opacity: 100 },
  defaultTo = { color: "#C6D92C", opacity: 20 },
  onChange,
}: ColorGradientPickerProps) {
  const [from, setFrom] = useState<ColorStop>(defaultFrom);
  const [to, setTo] = useState<ColorStop>(defaultTo);
  // FIX 1: useLayoutEffect stops the screen from flashing/shaking
  useLayoutEffect(() => {
    if (defaultFrom) {
      setFrom(defaultFrom);
    }
  }, [defaultFrom?.color, defaultFrom?.opacity]);

  useLayoutEffect(() => {
    if (defaultTo) {
      setTo(defaultTo);
    }
  }, [defaultTo?.color, defaultTo?.opacity]);

  useEffect(() => {
    onChange?.({ from, to });
  }, [from, to]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "100%",
      }}
    >
      <style>{`
                    input[type=number]::-webkit-inner-spin-button,
                    input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
                    input[type=number] { -moz-appearance: textfield; }
                `}</style>

      {/* FROM row */}
      <SingleColorRow value={from} onChange={setFrom} />

      {/* TO row */}
      <SingleColorRow value={to} onChange={setTo} />
    </div>
  );
}
